import React from 'react';

// Convert a plain-text value into JSX with detected URLs and phone numbers
// wrapped in anchor tags — mirrors what iOS does natively on a real Apple
// Wallet pass via data detectors. URLs become `target="_blank"` web links;
// phone numbers become `tel:` links so the device dials when tapped.
//
// Phone pattern handles:
//   - Australian local: 1300 369 265, 0412 345 678, (02) 9694 0400
//   - International: +61 2 9694 0400, (+61 2) 9694 0400
//   - Misc separators (must have ≥7 digits)
// Tries URL first (since `https://...` shouldn't be mis-detected as a phone).
const URL_PATTERN = /https?:\/\/[^\s)]+/g; // stops at whitespace OR ) so " (https://x) " keeps the paren
const PHONE_PATTERN = /\(?\+?\d[\d\s\-().]{6,}\d/g;

export function linkifyText(text: string, linkColor = '#7cb9ff'): React.ReactNode {
  if (!text) return text;

  // First pass: collect URL match ranges so phone-pattern can skip over them
  // (a phone-like substring inside a URL shouldn't get double-wrapped).
  const urlRanges: Array<[number, number, string]> = [];
  let um: RegExpExecArray | null;
  URL_PATTERN.lastIndex = 0;
  while ((um = URL_PATTERN.exec(text)) !== null) {
    urlRanges.push([um.index, um.index + um[0].length, um[0]]);
  }

  // Second pass: phone matches that don't overlap URL ranges
  const phoneRanges: Array<[number, number, string]> = [];
  let pm: RegExpExecArray | null;
  PHONE_PATTERN.lastIndex = 0;
  while ((pm = PHONE_PATTERN.exec(text)) !== null) {
    const start = pm.index;
    const end = pm.index + pm[0].length;
    const overlap = urlRanges.some(([s, e]) => start < e && end > s);
    if (!overlap) phoneRanges.push([start, end, pm[0]]);
  }

  const all = [
    ...urlRanges.map(([s, e, t]) => ({ s, e, t, type: 'url' as const })),
    ...phoneRanges.map(([s, e, t]) => ({ s, e, t, type: 'phone' as const })),
  ].sort((a, b) => a.s - b.s);

  if (all.length === 0) return text;

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  for (const r of all) {
    if (r.s > cursor) parts.push(text.slice(cursor, r.s));
    const href = r.type === 'url' ? r.t : `tel:${r.t.replace(/[\s\-().]/g, '')}`;
    parts.push(
      <a
        key={`lnk-${key++}`}
        href={href}
        className="underline"
        style={{ color: linkColor }}
        target={r.type === 'url' ? '_blank' : undefined}
        rel={r.type === 'url' ? 'noopener noreferrer' : undefined}
      >
        {r.t}
      </a>
    );
    cursor = r.e;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}
